import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import Vue from 'vue';
import axios from 'axios';
import { RootState } from '@/store/RootState';
import { settings } from '@/plugins/settings';
import responseHandler from '@/plugins/errorHandling';
import { IErrors } from '@/model/IErrors';
import {
  EServiceProviderOrderType,
  ECommissionType,
  IServiceProviderMonthlyMetric,
} from '@/model/ServiceProvider/serviceProviderMetricsInterfaces';

interface ServiceProviderMetricsState {
  metricsByPeriod: Record<string, IServiceProviderMonthlyMetric[]>;
  selectedYear: number;
  selectedMonth: number;
  closedPeriods: string[];
  errors: IErrors;
}

const now = new Date();

const state: ServiceProviderMetricsState = {
  metricsByPeriod: {},
  selectedYear: now.getFullYear(),
  selectedMonth: now.getMonth() + 1,
  closedPeriods: [],
  errors: {},
};

const periodKey = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}`;

const AUTO_GMV_TYPES = [EServiceProviderOrderType.AUTOMATIC];

const isPendingValidation = (m: IServiceProviderMonthlyMetric): boolean =>
  m.orderType === EServiceProviderOrderType.AUTOMATIC && m.orderCount === 0;

const isRowComplete = (m: IServiceProviderMonthlyMetric): boolean => {
  if (isPendingValidation(m)) return true;
  const gmvOk = AUTO_GMV_TYPES.includes(m.orderType) || m.gmv !== null;
  const commissionOk = m.commissionType !== ECommissionType.MANUAL || m.commissionManualValue !== null;
  return (
    m.scopeSize !== null &&
    m.visit !== null &&
    m.purchaseIntent !== null &&
    m.orderCount !== null &&
    gmvOk &&
    m.commissionRate !== null &&
    m.commissionType !== null &&
    commissionOk
  );
};

// Fields that should be cast to Number when updated
const NUMERIC_FIELDS: (keyof IServiceProviderMonthlyMetric)[] = [
  'scopeSize', 'visit', 'purchaseIntent', 'orderCount', 'gmv',
  'commissionRate', 'commissionManualValue',
];

const getters: GetterTree<ServiceProviderMetricsState, RootState> = {
  metricsList: (state): IServiceProviderMonthlyMetric[] => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    return state.metricsByPeriod[key] || [];
  },

  selectedPeriod: (state) => ({
    year: state.selectedYear,
    month: state.selectedMonth,
  }),

  isPeriodClosed: (state): boolean => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    return state.closedPeriods.includes(key);
  },

  isAllComplete: (state): boolean => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key] || [];
    if (metrics.length === 0) return false;
    return metrics.every(isRowComplete);
  },

  incompleteCount: (state): number => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key] || [];
    return metrics.filter((m) => !isRowComplete(m)).length;
  },

  pendingCount: (state): number => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key] || [];
    return metrics.filter((m) => isPendingValidation(m)).length;
  },

  awaitingValidationCount: (state): number => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key] || [];
    return metrics.filter((m) => !isPendingValidation(m) && isRowComplete(m) && !m.isClosed).length;
  },

  errors: (state): IErrors => state.errors,
};

const actions: ActionTree<ServiceProviderMetricsState, RootState> = {
  initMetrics: ({ commit, state }, { year, month }): void => {
    commit('selectedPeriodSet', { year, month });
    const key = periodKey(year, month);
    if (!state.metricsByPeriod[key]) {
      responseHandler.handle(
        () => axios({ url: settings.endpoint + 'serviceProviders/metrics', method: 'GET', params: { year, month } }),
        commit,
        'metricsLoaded'
      );
    }
  },

  setSelectedPeriod: ({ dispatch }, { year, month }): void => {
    dispatch('initMetrics', { year, month });
  },

  updateMetricField: (
    { commit },
    payload: { serviceProviderId: number; field: keyof IServiceProviderMonthlyMetric; value: any }
  ): void => {
    commit('metricFieldUpdated', payload);
  },

  saveRow: ({ commit, state }, serviceProviderId: number): void => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metric = (state.metricsByPeriod[key] || []).find((m) => m.serviceProviderId === serviceProviderId);
    if (!metric) return;
    responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'serviceProviders/' + serviceProviderId + '/metrics/' + state.selectedYear + '/' + state.selectedMonth,
        method: 'PUT',
        data: {
          scopeSize: metric.scopeSize,
          visit: metric.visit,
          purchaseIntent: metric.purchaseIntent,
          orderType: metric.orderType,
          orderCount: metric.orderCount,
          gmv: metric.gmv,
          commissionRate: metric.commissionRate,
          commissionType: metric.commissionType,
          commissionManualValue: metric.commissionManualValue,
        },
      }),
      commit,
      'metricSaved'
    );
  },

  closeMonth: ({ commit, state }): void => {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key] || [];
    const pendingRows = metrics
      .filter(isPendingValidation)
      .map((m) => ({
        serviceProviderId: m.serviceProviderId,
        scopeSize: m.scopeSize,
        visit: m.visit,
        purchaseIntent: m.purchaseIntent,
        orderType: m.orderType,
        orderCount: m.orderCount,
        gmv: m.gmv,
        commissionRate: m.commissionRate,
        commissionType: m.commissionType,
        commissionManualValue: m.commissionManualValue,
      }));
    responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'serviceProviders/metrics/' + state.selectedYear + '/' + state.selectedMonth + '/close',
        method: 'POST',
        data: { pendingRows },
      }),
      commit,
      'monthClosed'
    );
  },
};

const mutations: MutationTree<ServiceProviderMetricsState> = {
  selectedPeriodSet(state, { year, month }: { year: number; month: number }) {
    state.selectedYear = year;
    state.selectedMonth = month;
  },

  metricsLoaded(state, payload: { isClosed: boolean; metrics: IServiceProviderMonthlyMetric[] }) {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = ((payload && payload.metrics) || []).map((m) => {
      const orderType = m.orderType || EServiceProviderOrderType.ESTIMATE;
      const isZeroAutomatic = orderType === EServiceProviderOrderType.AUTOMATIC && m.orderCount === 0;
      return {
        ...m,
        orderType,
        commissionType: isZeroAutomatic ? ECommissionType.MANUAL : (m.commissionType || ECommissionType.ESTIMATED),
        commissionManualValue: isZeroAutomatic ? 0 : m.commissionManualValue,
      };
    });
    Vue.set(state.metricsByPeriod, key, metrics);
    if (payload && payload.isClosed && !state.closedPeriods.includes(key)) {
      state.closedPeriods.push(key);
    }
  },

  metricSaved(state, metric: IServiceProviderMonthlyMetric) {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key];
    if (!metrics) return;
    const idx = metrics.findIndex((m) => m.serviceProviderId === metric.serviceProviderId);
    if (idx !== -1) Vue.set(metrics, idx, metric);
  },

  metricFieldUpdated(
    state,
    { serviceProviderId, field, value }: { serviceProviderId: number; field: keyof IServiceProviderMonthlyMetric; value: any }
  ) {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key];
    if (!metrics) return;
    const idx = metrics.findIndex((m) => m.serviceProviderId === serviceProviderId);
    if (idx === -1) return;

    let parsed: any;
    if (value === '' || value === null || value === undefined) {
      parsed = null;
    } else if (NUMERIC_FIELDS.includes(field)) {
      parsed = Number(value);
    } else {
      parsed = value;
    }

    Vue.set(metrics[idx], field, parsed);
  },

  monthClosed(state, _payload: any) {
    const key = periodKey(state.selectedYear, state.selectedMonth);
    const metrics = state.metricsByPeriod[key];
    if (metrics) {
      metrics.forEach((m) => Vue.set(m, 'isClosed', true));
    }
    if (!state.closedPeriods.includes(key)) {
      state.closedPeriods.push(key);
    }
  },

  errors(state, payload: IErrors) {
    state.errors = payload;
  },
};

const namespaced = true;

export const serviceProviderMetricsModule: Module<ServiceProviderMetricsState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
